/**
 * Новый договор кредита, лизинга или банковской гарантии: проверка полей формы и итог
 * автографика до записи — сколько платежей, первая и последняя дата. Даты графика — по
 * тем же правилам, что сервер (`osnova-storage/src/ceo/debts.rs`, `auto_schedule_dates`):
 * «день платежа» каждого месяца, начиная с месяца после выдачи, не позже даты окончания;
 * 29–31 в коротком месяце — последний день; у договора, начатого раньше, — с ближайшего
 * дня платежа после сегодняшнего. Модуль без импортов значений — проверяется node:test
 * (`addDebtModel.test.mjs`); разбор сумм и ставок передаёт форма.
 */

/** Наибольший срок договора для автографика, месяцев (как на сервере). */
export const MAX_AUTO_SCHEDULE_MONTHS = 360;

/** Поля формы — как введены. */
export interface DebtForm {
  instType: string;
  contractNo: string;
  creditor: string;
  title: string;
  totalLimit: string;
  principalBalance: string;
  rateType: string;
  baseRate: string;
  marginRate: string;
  startDate: string;
  endDate: string;
  paymentDay: string;
  leasedAssetName: string;
  leasedAssetCount: string;
  collateral: string;
  covenants: string;
  autoGenerateSchedule: boolean;
}

/** Пустая форма: ставки, даты и день платежа не подставляются. */
export const EMPTY_DEBT_FORM: DebtForm = {
  instType: 'credit_line_vkl',
  contractNo: '',
  creditor: '',
  title: '',
  totalLimit: '',
  principalBalance: '',
  rateType: 'floating_cbr',
  baseRate: '',
  marginRate: '',
  startDate: '',
  endDate: '',
  paymentDay: '',
  leasedAssetName: '',
  leasedAssetCount: '1',
  collateral: '',
  covenants: '',
  autoGenerateSchedule: true,
};

/** Разбор сумм и ставок из поля ввода (`parseMoney`, `parseDecimal`). */
export interface DebtParsers {
  money: (raw: string) => number | null;
  decimal: (raw: string) => number | null;
}

/** Тело запроса создания договора. */
export interface DebtPayload {
  instrument_type: string;
  contract_number: string;
  title: string;
  creditor_name: string;
  total_limit: number;
  drawn_amount: number;
  principal_balance: number;
  rate_type: string;
  base_rate_pct: number;
  margin_pct: number;
  start_date: string;
  end_date: string;
  payment_day?: number;
  leased_asset_name?: string;
  leased_asset_count: number;
  collateral_summary?: string;
  covenants_summary?: string;
  auto_generate_schedule: boolean;
}

/** Ошибки у полей; `schedule` — у итога графика. */
export type DebtFormErrors = Partial<Record<keyof DebtForm | 'schedule', string>>;

/** Итог автографика до записи. */
export interface SchedulePreview {
  /** `quarterly` — комиссии по гарантии раз в квартал. */
  kind: 'monthly' | 'quarterly';
  count: number;
  first: string;
  last: string;
  /** Платежей не будет (остаток долга или ставка комиссии — ноль). */
  empty: string | null;
}

export interface DebtFormCheck {
  errors: DebtFormErrors;
  /** Есть, только когда ошибок нет. */
  payload: DebtPayload | null;
  preview: SchedulePreview | null;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** «2026-11-03» → «03.11.2026». */
export function ruDate(iso: string): string {
  const m = ISO.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** «День платежа» (1–31) в месяце; 29–31 в коротком месяце — последний день месяца. */
export function paymentDateInMonth(year: number, month: number, day: number): string {
  const d = Math.min(Math.max(day, 1), 31, daysInMonth(year, month));
  return `${year}-${pad(month)}-${pad(d)}`;
}

/** Даты автографика или текст, почему график не строится (тексты — как у сервера). */
export function autoScheduleDates(
  start: string,
  end: string,
  paymentDay: number,
  today: string,
): { dates: string[] } | { error: string } {
  const s = ISO.exec(start);
  const e = ISO.exec(end);
  if (!s || !e) return { error: 'Укажите даты начала и окончания договора' };
  const sy = Number(s[1]);
  const sm = Number(s[2]);
  const term = (Number(e[1]) - sy) * 12 + (Number(e[2]) - sm);
  if (term > MAX_AUTO_SCHEDULE_MONTHS) {
    return {
      error: `Срок договора больше ${MAX_AUTO_SCHEDULE_MONTHS / 12} лет — график автоматически не строится. Загрузите график платежей из Excel`,
    };
  }
  const dates: string[] = [];
  for (let offset = 1; offset <= term; offset += 1) {
    const total = sy * 12 + (sm - 1) + offset;
    const date = paymentDateInMonth(Math.floor(total / 12), (total % 12) + 1, paymentDay);
    if (date > today && date <= end) dates.push(date);
  }
  if (dates.length === 0) {
    const endRu = ruDate(end);
    return {
      error:
        end <= today
          ? `Договор уже закончился (${endRu}) — автографик строится только на будущие платежи. Снимите галочку графика или загрузите график из Excel`
          : `До даты окончания ${endRu} нет ни одного платежа ${paymentDay}-го числа: первый платёж — в месяце после выдачи и не раньше завтрашнего дня. Проверьте даты договора и день платежа или снимите галочку графика`,
    };
  }
  return { dates };
}

/** Даты комиссий по гарантии: каждая третья дата, последняя — в последнюю (неполный квартал). */
export function guaranteeFeeDates(dates: readonly string[]): string[] {
  const out: string[] = [];
  for (let q = 1; q <= Math.ceil(dates.length / 3); q += 1) {
    out.push(dates[Math.min(q * 3, dates.length) - 1]);
  }
  return out;
}

/** 1 платёж, 2 платежа, 5 платежей. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Ставка или маржа: «-1,5» — маржа может быть и отрицательной. */
function parseSigned(raw: string, decimal: DebtParsers['decimal']): number | null {
  const s = raw.trim();
  const minus = /^[-−–]/.test(s);
  const n = decimal(minus ? s.slice(1) : s);
  return n === null ? null : minus ? -n : n;
}

/** День платежа: целое 1–31, пусто — `undefined`, иначе `null`. */
function parseDay(raw: string): number | undefined | null {
  const s = raw.trim();
  if (!s) return undefined;
  if (!/^\d{1,2}$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= 31 ? n : null;
}

/**
 * Проверка формы: ошибки у полей, тело запроса и итог графика. Ноль — допустимое значение
 * (беспроцентный договор), пустое поле — ошибка; значения по умолчанию не подставляются.
 */
export function checkDebtForm(form: DebtForm, parse: DebtParsers, today: string): DebtFormCheck {
  const errors: DebtFormErrors = {};
  const guarantee = form.instType === 'bank_guarantee';
  const leasing = form.instType === 'leasing';
  const floating = form.rateType === 'floating_cbr';

  if (!form.contractNo.trim()) errors.contractNo = 'Укажите номер договора';
  if (!form.creditor.trim()) {
    errors.creditor = guarantee ? 'Укажите банк-гарант' : 'Укажите кредитора или лизингодателя';
  }
  if (!form.title.trim()) errors.title = 'Укажите наименование или цель финансирования';

  const limit = parse.money(form.totalLimit);
  if (limit === null) {
    errors.totalLimit = form.totalLimit.trim()
      ? 'Лимит — числом, например 12 000 000'
      : guarantee
        ? 'Укажите лимит гарантии'
        : 'Укажите лимит договора';
  }
  const balanceRaw = form.principalBalance.trim();
  const balanceParsed = balanceRaw ? parse.money(form.principalBalance) : undefined;
  if (balanceParsed === null) {
    errors.principalBalance = 'Остаток долга — числом, например 4 500 000; пусто — равен лимиту';
  }

  const baseRate = parse.decimal(form.baseRate);
  if (baseRate === null) {
    errors.baseRate = form.baseRate.trim()
      ? 'Ставка — числом, например 18,25'
      : 'Укажите ставку, для беспроцентного договора — 0';
  }
  // Маржа — только у плавающей ставки (КС ЦБ + маржа).
  const margin = floating ? parseSigned(form.marginRate, parse.decimal) : 0;
  if (margin === null) {
    errors.marginRate = form.marginRate.trim()
      ? 'Маржа — числом, например 2,5'
      : 'Укажите маржу банка, без маржи — 0';
  }

  if (!form.startDate) errors.startDate = 'Укажите дату начала договора';
  if (!form.endDate) errors.endDate = 'Укажите дату окончания договора';
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    errors.endDate = 'Дата окончания раньше даты начала';
  }

  const day = parseDay(form.paymentDay);
  if (day === null) {
    errors.paymentDay = 'День платежа — число от 1 до 31';
  } else if (day === undefined && form.autoGenerateSchedule) {
    errors.paymentDay = 'Укажите день платежа от 1 до 31 — по нему строится график';
  }

  let count = 0;
  if (leasing) {
    const s = form.leasedAssetCount.trim();
    if (!/^\d{1,5}$/.test(s)) {
      errors.leasedAssetCount = s
        ? 'Количество — целым числом, например 2'
        : 'Укажите количество единиц техники';
    } else {
      count = Number(s);
    }
  }

  const lim = limit ?? 0;
  const balance = balanceParsed ?? (guarantee ? 0 : lim);

  let preview: SchedulePreview | null = null;
  const datesReady =
    form.autoGenerateSchedule &&
    typeof day === 'number' &&
    !errors.startDate &&
    !errors.endDate;
  if (datesReady) {
    const result = autoScheduleDates(form.startDate, form.endDate, day, today);
    if ('error' in result) {
      errors.schedule = result.error;
    } else {
      const dates = guarantee ? guaranteeFeeDates(result.dates) : result.dates;
      const empty = guarantee
        ? lim <= 0
          ? 'Лимит гарантии — ноль: комиссий нет, график не строится'
          : baseRate === 0
            ? 'Ставка комиссии 0 %: комиссий нет, график не строится'
            : null
        : balance <= 0
          ? 'Остаток долга — ноль: платежей нет, график не строится'
          : null;
      preview = {
        kind: guarantee ? 'quarterly' : 'monthly',
        count: dates.length,
        first: dates[0],
        last: dates[dates.length - 1],
        empty,
      };
    }
  }

  if (Object.keys(errors).length > 0) return { errors, payload: null, preview };

  const payload: DebtPayload = {
    instrument_type: form.instType,
    contract_number: form.contractNo.trim(),
    title: form.title.trim(),
    creditor_name: form.creditor.trim(),
    total_limit: lim,
    drawn_amount: guarantee ? lim : balance,
    principal_balance: balance,
    rate_type: form.rateType,
    base_rate_pct: baseRate ?? 0,
    margin_pct: margin ?? 0,
    start_date: form.startDate,
    end_date: form.endDate,
    payment_day: typeof day === 'number' ? day : undefined,
    leased_asset_name: leasing ? form.leasedAssetName.trim() || undefined : undefined,
    leased_asset_count: leasing ? count : 0,
    collateral_summary: form.collateral.trim() || undefined,
    covenants_summary: form.covenants.trim() || undefined,
    auto_generate_schedule: form.autoGenerateSchedule,
  };
  return { errors, payload, preview };
}
